import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Terminal, UserPlus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useProjects, type Project } from "@/hooks/useProjects";
import { useOnboardingTrigger } from "@/hooks/useOnboardingTrigger";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";

interface CommandStepProps {
  index: number;
  title: string;
  hint: string;
  command: string;
}

function CommandStep({ index, title, hint, command }: CommandStepProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard API unavailable (non-HTTPS or permissions denied).
    });
  }, [command]);

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4">
      <div className="flex items-center gap-2">
        <Terminal className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-medium">{index}. {title}</p>
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>

      <pre className="overflow-x-auto rounded-md bg-background p-3 font-mono text-sm">
        {command}
      </pre>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleCopy}>
          <Copy className="h-3 w-3" />
          {copied ? t("onboarding.copied") : t("onboarding.copy")}
        </Button>
      </div>
    </div>
  );
}

export function OnboardingPage() {
  const { t } = useTranslation();
  const { projects, loading: projectsLoading, error: projectsError } = useProjects();
  const { trigger, loading: triggerLoading, error: triggerError } = useOnboardingTrigger();
  const { org } = useOrganization();

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    if (triggerError) toast.error(triggerError);
  }, [triggerError]);

  const handleProjectChange = useCallback(
    async (slug: string) => {
      const project = projects.find((p) => p.slug === slug) ?? null;
      setSelectedProject(project);
      if (project) {
        await trigger(project.slug);
      }
    },
    [projects, trigger],
  );

  const serverUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";
  const connectCommand = org?.slug
    ? (serverUrl ? `rai connect --server ${serverUrl} ${org.slug}` : `rai connect ${org.slug}`)
    : null;

  const initCommand = selectedProject
    ? `rai init --slug ${selectedProject.slug} --apply`
    : null;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader icon={UserPlus} title={t("onboarding.title")} subtitle={t("onboarding.subtitle")} secondaryGlow={false} />

      {/* Project selector */}
      <div className="space-y-2">
        <label htmlFor="project-select" className="text-sm font-medium">
          {t("onboarding.selectProject")}
        </label>
        {projectsLoading ? (
          <p className="text-sm text-muted-foreground">{t("onboarding.loadingProjects")}</p>
        ) : projectsError ? (
          <p className="text-sm text-destructive">{projectsError}</p>
        ) : (
          <Select
            value={selectedProject?.slug ?? ""}
            onValueChange={handleProjectChange}
            disabled={triggerLoading}
          >
            <SelectTrigger id="project-select">
              <SelectValue placeholder={t("onboarding.selectPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.slug}>
                  {p.name} ({p.slug})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {triggerError && <p className="text-sm text-destructive">{triggerError}</p>}
      </div>

      {connectCommand && (
        <CommandStep
          index={1}
          title={t("onboarding.steps.connect.title")}
          hint={t("onboarding.steps.connect.hint")}
          command={connectCommand}
        />
      )}
      {initCommand && (
        <>
          <CommandStep
            index={2}
            title={t("onboarding.steps.init.title")}
            hint={t("onboarding.steps.init.hint")}
            command={initCommand}
          />
          <CommandStep
            index={3}
            title={t("onboarding.steps.push.title")}
            hint={t("onboarding.steps.push.hint")}
            command="rai graph push"
          />
        </>
      )}
    </div>
  );
}
